import React from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo, AppCredit } from "@/components/Logo";
import { TAGLINE } from "@/lib/branding";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // TAMPILAN-SOQ (Variasi A): blue panel with the logo, tagline, orange slanted
  // stripes and the machine line drawing (placeholder for the shop photo); the
  // form on white. The one-time entrance animation is CSS only (index.css,
  // .login-anim-*), under 1 s, never blocks typing, off with reduced motion.
  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[1.1fr_1fr]">
      <div className="login-panel relative overflow-hidden bg-soq-biru text-white px-6 pt-10 pb-16 lg:p-12 flex flex-col justify-center">
        <div className="latar-mesin absolute inset-0 pointer-events-none" aria-hidden />
        <span aria-hidden className="login-anim-sapu absolute top-0 bottom-0 -right-16 w-[150px] lg:w-[150px] bg-soq-oranye [transform:skewX(-12deg)]" />
        <span aria-hidden className="login-anim-sapu login-anim-sapu-2 hidden lg:block absolute top-0 bottom-0 right-[110px] w-[22px] bg-soq-oranye [transform:skewX(-12deg)]" />
        <div className="relative">
          <Logo varian="putih" className="login-anim-logo h-20 sm:h-24 lg:h-28 w-auto" />
          <p data-testid="login-tagline" className="login-anim-tagline mt-5 max-w-[26rem] pr-16 lg:pr-0 font-judul text-2xl lg:text-[30px] leading-tight uppercase">
            {TAGLINE}
          </p>
        </div>
      </div>
      <div className="relative flex flex-col items-center justify-center px-4 py-10 -mt-6 lg:mt-0">
        <div className="login-anim-form w-full max-w-md">
        <form
          data-testid="login-form"
          onSubmit={onSubmit}
          className="rounded-xl border border-slate-800 bg-white p-6 sm:p-8 shadow-[0_12px_32px_rgba(16,32,208,0.10)] space-y-5"
        >
          <div>
            <h2 className="font-judul text-[28px] leading-none uppercase text-soq-biru">Masuk</h2>
            <p className="text-sm text-slate-500 mt-2">
              Gunakan akun yang diberikan admin.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-100">
              Username
            </label>
            <input
              data-testid="login-username-input"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full min-h-[44px] rounded-lg border border-slate-600 bg-white px-4 py-2.5 text-[15px] text-slate-100 outline-none focus:border-soq-biru focus:ring-2 focus:ring-soq-biru/25 transition-colors"
              placeholder="username"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-100">
              Password
            </label>
            <input
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full min-h-[44px] rounded-lg border border-slate-600 bg-white px-4 py-2.5 text-[15px] text-slate-100 outline-none focus:border-soq-biru focus:ring-2 focus:ring-soq-biru/25 transition-colors"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg bg-soq-oranye px-4 py-2.5 text-[15px] font-semibold text-soq-biru-tua transition-colors duration-200 hover:bg-amber-400 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <LogIn size={16} />
            )}
            {loading ? "Memproses…" : "Login"}
          </button>
        </form>
        <AppCredit className="mt-6" />
        </div>
      </div>
    </div>
  );
}
